import { Col, Row } from "antd";
import cuid from "cuid";
import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCallReceived,
  MdOutlinePinDrop,
  MdOutlineQrCode,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  PAYMENT_RECEIVER,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Refund = ({
  setDisabled,
  setPercent,
  bindField,
  initialValues,
  isFieldRequired,
}) => {
  const [receiverZipCode, setReceiverZipCode] = useState(() =>
    get(
      initialValues,
      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    ),
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { cities, loading: citiesLoading } =
    useZipCodeCities(receiverZipCode);

  const hasPayerBankDetails =
    get(initialValues, "payment.payer.IBAN") &&
    get(initialValues, "payment.payer.BIC");

  const PAYMENT_RECEIVER_VALUES = {
    [PAYMENT_RECEIVER.SAME_ACCOUNT]: () => {
      set(
        initialValues,
        "payment.receiver",
        get(initialValues, "payment.payer"),
      );
      set(
        initialValues,
        "telecoms.receiver",
        get(initialValues, "telecoms.payer"),
      );
    },
    [PAYMENT_RECEIVER.ANOTHER_ACCOUNT]: () => {
      set(initialValues, "payment.receiver", {
        id: cuid(),
        owner: { id: cuid() },
      });
      if (!get(initialValues, "telecoms.receiver")) {
        set(initialValues, "telecoms.receiver", {});
      }
    },
  };

  const isPayerReceiverPath = "isPayerReceiver";
  const isPayerReceiverField = bindField(isPayerReceiverPath, {
    label: SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT.LABEL,
    required: isFieldRequired(isPayerReceiverPath),
    disabled: !hasPayerBankDetails,
    onChange: (value) => {
      const isSameAccount = value === PAYMENT_RECEIVER.SAME_ACCOUNT;
      setDisabled((prev) => ({
        ...prev,
        proof: {
          ...prev.proof,
          refundAccount: isSameAccount,
        },
        payment: {
          ...prev.payment,
          receiver: {
            ...get(prev, "payment.receiver", {}),
            owner: {
              firstName: isSameAccount,
              lastName: isSameAccount,
            },
            IBAN: isSameAccount,
            BIC: isSameAccount,
          },
        },
        telecoms: {
          ...prev.telecoms,
          receiver: {
            [CONTACT_POINT_SYSTEM.ADDRESS]: { value: isSameAccount },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: isSameAccount },
            [CONTACT_POINT_SYSTEM.CITY]: { value: isSameAccount },
          },
        },
      }));
      PAYMENT_RECEIVER_VALUES[value]();
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "payment.receiver.owner.lastName";
  const lastNameField = bindField(lastNamePath, {
    label: SUBSCRIPTION.FORM.OWNER.LASTNAME.LABEL,
    required: isFieldRequired(lastNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstNamePath = "payment.receiver.owner.firstName";
  const firstNameField = bindField(firstNamePath, {
    label: SUBSCRIPTION.FORM.OWNER.FIRSTNAME.LABEL,
    required: isFieldRequired(firstNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const ibanPath = "payment.receiver.IBAN";
  const ibanField = bindField(ibanPath, {
    label: SUBSCRIPTION.FORM.IBAN.LABEL,
    required: isFieldRequired(ibanPath),
    onBlur: () => setPercent(initialValues),
  });

  const bicPath = "payment.receiver.BIC";
  const bicField = bindField(bicPath, {
    label: SUBSCRIPTION.FORM.BIC.LABEL,
    required: isFieldRequired(bicPath),
    onBlur: () => setPercent(initialValues),
  });

  const addressPath = `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`;
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.ADDRESS.LABEL,
    required: isFieldRequired(addressPath),
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    required: isFieldRequired(complementPath),
    onBlur: () => setPercent(initialValues),
  });

  const zipCodePath = `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    onChange: (value) => {
      set(
        initialValues,
        `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
        undefined,
      );
      setReceiverZipCode(value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const cityPath = `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={isPayerReceiverField.path}
              label={isPayerReceiverField.label}
              required={isPayerReceiverField.required}
              disabled={isPayerReceiverField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlineCallReceived size={16} />}
                  label={isPayerReceiverField.label}
                  value={get(initialValues, isPayerReceiverPath)}
                  values={[
                    {
                      value: PAYMENT_RECEIVER.SAME_ACCOUNT,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .OPTIONS.USER_ACCOUNT,
                      popover:
                        !hasPayerBankDetails &&
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .POPOVER,
                    },
                    {
                      value: PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .OPTIONS.OTHER,
                    },
                  ]}
                  {...field}
                  onBlur={isPayerReceiverField.onBlur}
                  onChange={isPayerReceiverField.onChange}
                />
              )}
            </FormField>
          </Col>
          {get(initialValues, "isPayerReceiver") ===
            PAYMENT_RECEIVER.ANOTHER_ACCOUNT && (
            <>
              <Col lg={12} xs={12}>
                <FormField
                  path={lastNameField.path}
                  label={lastNameField.label}
                  required={lastNameField.required}
                  disabled={lastNameField.disabled}
                >
                  {(field) => (
                    <Input
                      label={lastNameField.label}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={SUBSCRIPTION.FORM.OWNER.LASTNAME.PLACEHOLDER}
                      type="custom"
                      normalizeText={true}
                      defaultValue={lastNameField.defaultValue}
                      {...field}
                      onChange={lastNameField.onChange}
                      onBlur={lastNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={12} xs={12}>
                <FormField
                  path={firstNameField.path}
                  label={firstNameField.label}
                  required={firstNameField.required}
                  disabled={firstNameField.disabled}
                >
                  {(field) => (
                    <Input
                      label={firstNameField.label}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={
                        SUBSCRIPTION.FORM.OWNER.FIRSTNAME.PLACEHOLDER
                      }
                      type="custom"
                      normalizeText={true}
                      defaultValue={firstNameField.defaultValue}
                      {...field}
                      onChange={firstNameField.onChange}
                      onBlur={firstNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={ibanField.path}
                  label={ibanField.label}
                  required={ibanField.required}
                  disabled={ibanField.disabled}
                >
                  {(field) => (
                    <Input
                      label={ibanField.label}
                      icon={<MdOutlineQrCode size={16} />}
                      type="custom"
                      trim={true}
                      uppercase={true}
                      placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                      defaultValue={ibanField.defaultValue}
                      {...field}
                      onChange={ibanField.onChange}
                      onBlur={ibanField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={bicField.path}
                  label={bicField.label}
                  required={bicField.required}
                  disabled={bicField.disabled}
                >
                  {(field) => (
                    <Input
                      label={bicField.label}
                      icon={<MdOutlineQrCode size={16} />}
                      type="custom"
                      trim={true}
                      uppercase={true}
                      placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                      defaultValue={bicField.defaultValue}
                      {...field}
                      onChange={bicField.onChange}
                      onBlur={bicField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={addressField.path}
                  label={addressField.label}
                  required={addressField.required}
                  disabled={addressField.disabled}
                >
                  {(field) => (
                    <Input
                      label={addressField.label}
                      icon={<MdOutlineAddRoad size={16} />}
                      placeholder={SUBSCRIPTION.FORM.ADDRESS.PLACEHOLDER}
                      type="custom"
                      normalizeText={true}
                      defaultValue={addressField.defaultValue}
                      {...field}
                      onChange={addressField.onChange}
                      onBlur={addressField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={complementField.path}
                  label={complementField.label}
                  required={complementField.required}
                  disabled={complementField.disabled}
                >
                  {(field) => (
                    <Input
                      label={complementField.label}
                      optional={SUBSCRIPTION.FORM.OPTIONAL}
                      icon={<MdOutlineAddRoad size={16} />}
                      placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                      type="custom"
                      normalizeText={true}
                      defaultValue={complementField.defaultValue}
                      {...field}
                      onChange={complementField.onChange}
                      onBlur={complementField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={zipCodeField.path}
                  label={zipCodeField.label}
                  required={zipCodeField.required}
                  disabled={zipCodeField.disabled}
                >
                  {(field) => (
                    <Input
                      label={zipCodeField.label}
                      icon={<MdOutlinePinDrop size={16} />}
                      placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                      defaultValue={zipCodeField.defaultValue}
                      {...field}
                      onChange={zipCodeField.onChange}
                      onBlur={zipCodeField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={cityField.path}
                  label={cityField.label}
                  required={cityField.required}
                  disabled={cityField.disabled}
                >
                  {(field) => (
                    <Select
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={cityField.label}
                      placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                      showSearch
                      loading={citiesLoading}
                      options={cities}
                      defaultValue={cityField.defaultValue}
                      {...field}
                      onChange={cityField.onChange}
                      onBlur={cityField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Refund;
